import React, { useState, useEffect, useMemo, memo } from 'react';
import OpportunityCard from './OpportunityCard';
import './ResultsSection.scss';

const PAGE_SIZE = 8;

const FILTER_LABELS = {
  age:       'Age',
  pay:       'Pay',
  time:      'Time',
  volunteer: 'Focus Area',
  education: 'Education',
};

function ResultsSection({ data, activeFilter, ageGroup, payType, timeType, educationLevel, focusArea, onResetFilter }) {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Reset to first page whenever the filtered data changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [data]);

  const visible = useMemo(() => data.slice(0, visibleCount), [data, visibleCount]);
  const hasMore = visibleCount < data.length;

  const isFiltered = activeFilter && activeFilter !== 'latest';
  const filterLabel = useMemo(() => {
    let label = FILTER_LABELS[activeFilter] || null;
    if (activeFilter === 'age' && ageGroup) {
      label = ageGroup === '16-17' ? '16-17 Years' : '18+ Years';
    }
    if (activeFilter === 'pay' && payType) {
      label = payType === 'academic credit'
        ? 'Academic Credit'
        : payType.charAt(0).toUpperCase() + payType.slice(1);
    }
    if (activeFilter === 'time' && timeType) {
      label = timeType === 'both'
        ? 'Both'
        : timeType.charAt(0).toUpperCase() + timeType.slice(1);
    }
    if (activeFilter === 'volunteer' && focusArea) {
      label = focusArea;
    }
    if (activeFilter === 'education' && educationLevel) {
      label = educationLevel === 'high school/ged'
        ? 'High school/GED'
        : educationLevel.charAt(0).toUpperCase() + educationLevel.slice(1);
    }
    return label;
  }, [activeFilter, ageGroup, payType, timeType, focusArea, educationLevel]);

  return (
    <section className="results-section" aria-label="Opportunities">
      {isFiltered && filterLabel && (
        <div className="results-header">
          <>
            {data.length > 0 && (
              <div className="results-filter-box" aria-live="polite" aria-atomic="true">
                <span>
                  <strong>{filterLabel}</strong>
                  <span className="results-filter-count"> ({data.length})</span>
                </span>
                <button className="results-filter-clear" onClick={onResetFilter} aria-label="Clear filters">
                  ×
                </button>
              </div>
            )}
          </>
        </div>
      )}

      <div className="results-grid" role="list" aria-live="polite" aria-atomic="false">
        {data.length === 0 ? (
          <div className="no-results" role="status">
            <p>No opportunities found. Try adjusting your filters.</p>
            {isFiltered && (
              <button className="no-results-reset" onClick={onResetFilter}>
                Reset View
              </button>
            )}
          </div>
        ) : (
          visible.map(item => (
            <OpportunityCard key={item.id} item={item} />
          ))
        )}
      </div>

      {hasMore && (
        <div className="show-more-row">
          <button
            className="btn-show-more"
            onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
            aria-label={`Show more opportunities, showing ${visibleCount} of ${data.length}`}
          >
            Show Me More!
          </button>
        </div>
      )}
    </section>
  );
}

export default memo(ResultsSection);
